import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, CreditCard, Building2, Copy, Mail, RefreshCw, Lock } from "lucide-react";
import { api, LOGO_URL } from "../lib/config";

export default function RegisterPage() {
  const nav = useNavigate();
  // step 0: form+consents, 1: OTP email verify, 2: payment method, 3: bonifico
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ nome: "", cognome: "", email: "", telefono: "" });
  const [consents, setConsents] = useState({ privacy: false, terms: false, age: false });
  const [settings, setSettings] = useState(null);
  const [otp, setOtp] = useState("");
  const [emailToken, setEmailToken] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const amount = settings?.registration_amount ?? 60;

  const validateForm = () => {
    if (!data.nome.trim()) return "Inserisci il nome";
    if (!data.cognome.trim()) return "Inserisci il cognome";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) return "Inserisci una email valida";
    if (!data.telefono.trim() || data.telefono.trim().length < 6) return "Inserisci un numero di telefono valido";
    if (!consents.age) return "Devi confermare di essere maggiorenne";
    if (!consents.privacy) return "Devi accettare la Privacy Policy per procedere";
    if (!consents.terms) return "Devi accettare i Termini e Condizioni per procedere";
    return null;
  };

  const requestOtp = async () => {
    try {
      await api.post("/otp/email/send", { email: data.email });
      setResendCooldown(30);
      toast.success(`Codice di verifica inviato a ${data.email}`);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio codice");
      return false;
    }
  };

  const next = async () => {
    const err = validateForm();
    if (err) { toast.error(err); return; }
    setLoading(true);
    if (await requestOtp()) setStep(1);
    setLoading(false);
  };

  const verifyOtp = async () => {
    if (otp.length !== 6) { toast.error("Inserisci il codice a 6 cifre"); return; }
    setLoading(true);
    try {
      const r = await api.post("/otp/email/verify", { email: data.email, code: otp });
      setEmailToken(r.data.verification_token);
      toast.success("Email verificata!");
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Codice errato");
    }
    setLoading(false);
  };

  const startOnlinePayment = async () => {
    if (!emailToken) { toast.error("Verifica email scaduta, ricomincia"); setStep(0); return; }
    setLoading(true);
    try {
      const res = await api.post("/registration/start", {
        ...data,
        origin_url: window.location.origin,
        email_verification_token: emailToken,
      });
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error("Errore nella creazione della sessione di pagamento");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore pagamento");
    }
    setLoading(false);
  };

  const copyText = async (t) => {
    try { await navigator.clipboard.writeText(t); toast.success("Copiato!"); } catch {}
  };

  // Guard: if registrations are closed, show a full-page closed state
  const registrationsClosed = settings && settings.registrations_open === false;

  if (registrationsClosed) {
    return (
      <div className="min-h-screen bg-fp-bg grid-bg relative flex items-center justify-center px-5 md:px-8 py-12" data-testid="register-page-closed">
        <div className="absolute inset-0 radial-glow opacity-40" />
        <div className="relative max-w-2xl w-full text-center">
          <button onClick={() => nav("/")} className="inline-flex items-center gap-2 text-white/60 hover:text-fp-cyan text-sm mb-8 transition-colors" data-testid="back-btn">
            <ArrowLeft size={16} /> Torna alla home
          </button>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-8 md:p-14 rounded-3xl border border-white/10 bg-fp-surface/50 backdrop-blur-sm tracing-border"
          >
            <div className="mx-auto w-20 h-20 rounded-3xl bg-fp-cyan/10 border border-fp-cyan/30 text-fp-cyan flex items-center justify-center mb-6">
              <Lock size={32} />
            </div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-fp-cyan font-heading font-semibold">
              Edizione 2026/2027
            </div>
            <h1 className="mt-3 font-heading font-black text-3xl md:text-5xl">
              <span className="silver-gradient">Iscrizioni</span>{" "}
              <span className="cyan-gradient">momentaneamente chiuse</span>
            </h1>
            <p className="mt-6 text-white/70 text-base md:text-lg leading-relaxed max-w-lg mx-auto" data-testid="registrations-closed-message">
              {settings.registrations_closed_message || "Le iscrizioni sono momentaneamente chiuse. Torna a trovarci presto: pubblicheremo qui la data di riapertura."}
            </p>
            <div className="mt-10 flex flex-wrap gap-3 justify-center">
              <button
                onClick={() => nav("/")}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-fp-cyan text-fp-bg font-heading font-bold uppercase tracking-wider text-sm hover:shadow-[0_0_25px_rgba(0,229,255,0.6)] transition-all"
                data-testid="closed-back-home-btn"
              >
                Torna alla home
              </button>
              <a
                href="https://wa.me/393778100717"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-white/10 text-white/80 hover:border-fp-cyan/50 hover:text-fp-cyan font-heading font-semibold uppercase tracking-wider text-sm transition-all"
                data-testid="closed-whatsapp-btn"
              >
                Contattaci su WhatsApp
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-fp-bg grid-bg relative" data-testid="register-page">
      <div className="absolute inset-0 radial-glow opacity-40" />
      <div className="relative max-w-3xl mx-auto px-5 md:px-8 py-12">
        <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-white/60 hover:text-fp-cyan text-sm mb-8 transition-colors" data-testid="back-btn">
          <ArrowLeft size={16} /> Torna indietro
        </button>

        <div className="flex items-center gap-4 mb-10">
          <img src={LOGO_URL} alt="" className="w-14 h-14" />
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-fp-cyan font-heading font-semibold">
              Iscrizione · Edizione 2026/2027
            </div>
            <h1 className="font-heading font-black text-3xl md:text-4xl silver-gradient">Unisciti al torneo</h1>
          </div>
        </div>

        {/* Steps bar */}
        <div className="flex items-center gap-2 mb-10">
          {[0, 1, 2].map((s) => (
            <div key={s} className={`h-1 flex-1 rounded-full transition-all ${step >= s ? "bg-fp-cyan shadow-[0_0_10px_rgba(0,229,255,0.5)]" : "bg-white/10"}`} />
          ))}
        </div>

        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div key="step-0" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="p-7 md:p-10 rounded-3xl border border-white/10 bg-fp-surface/50 backdrop-blur-sm tracing-border">
              <h2 className="font-heading font-bold text-2xl">I tuoi dati</h2>
              <p className="mt-2 text-white/60 text-sm">Queste informazioni vengono salvate solo dopo il pagamento. L'email sarà verificata con un codice OTP nel passo successivo.</p>
              <div className="mt-7 grid sm:grid-cols-2 gap-5">
                {[
                  { k: "nome", l: "Nome", p: "Mario" },
                  { k: "cognome", l: "Cognome", p: "Rossi" },
                  { k: "email", l: "Email", p: "mario@example.com", t: "email" },
                  { k: "telefono", l: "Numero di telefono", p: "+39 333 1234567", t: "tel" },
                ].map((f) => (
                  <div key={f.k} className={f.k === "email" || f.k === "telefono" ? "sm:col-span-2" : ""}>
                    <label className="text-[10px] tracking-[0.3em] uppercase text-fp-cyan font-heading font-semibold">{f.l}</label>
                    <input type={f.t || "text"} value={data[f.k]} onChange={(e) => setData({ ...data, [f.k]: e.target.value })}
                      className="mt-2 w-full px-4 py-3 rounded-xl bg-fp-bg/60 border border-white/10 focus:border-fp-cyan focus:outline-none text-white placeholder:text-white/30"
                      placeholder={f.p} data-testid={`input-${f.k}`} />
                  </div>
                ))}
              </div>

              <div className="mt-7 space-y-3 p-4 rounded-2xl bg-fp-bg/40 border border-white/10">
                {[
                  { k: "age", text: <>Dichiaro di aver compiuto <strong>18 anni</strong> e di possedere la piena capacità di agire.</> },
                  { k: "privacy", text: <>Ho letto e accetto la <Link to="/privacy" target="_blank" className="text-fp-cyan hover:underline">Privacy Policy</Link>.</> },
                  { k: "terms", text: <>Ho letto e accetto i <Link to="/termini" target="_blank" className="text-fp-cyan hover:underline">Termini e Condizioni</Link> e approvo specificamente, ai sensi degli artt. 1341 e 1342 c.c., le clausole su rimborsi, esclusione del recesso, squalifica, premi, limitazione di responsabilità e foro competente.</> },
                ].map((c) => (
                  <label key={c.k} className="flex items-start gap-3 cursor-pointer text-sm" data-testid={`consent-${c.k}-label`}>
                    <input type="checkbox" checked={consents[c.k]} onChange={(e) => setConsents({ ...consents, [c.k]: e.target.checked })}
                      className="mt-0.5 w-4 h-4 accent-fp-cyan flex-shrink-0" data-testid={`consent-${c.k}`} />
                    <span className="text-white/75 leading-relaxed">{c.text}</span>
                  </label>
                ))}
              </div>

              <button onClick={next} disabled={loading || !consents.age || !consents.privacy || !consents.terms}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-fp-cyan text-fp-bg font-heading font-bold tracking-wider uppercase text-sm hover:shadow-[0_0_25px_rgba(0,229,255,0.6)] hover:-translate-y-0.5 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="next-step-btn">
                {loading ? "Invio codice..." : <>Verifica email <ArrowRight size={16} /></>}
              </button>
            </motion.div>
          )}

          {step === 1 && (
            <motion.div key="step-1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
              className="p-7 md:p-10 rounded-3xl border border-white/10 bg-fp-surface/50 backdrop-blur-sm tracing-border" data-testid="otp-step">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-fp-cyan/10 border border-fp-cyan/30 text-fp-cyan flex items-center justify-center">
                  <Mail size={22} />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-2xl">Verifica la tua email</h2>
                  <p className="text-white/60 text-sm">Codice inviato a <span className="text-white">{data.email}</span></p>
                </div>
              </div>
              <div className="mt-7">
                <label className="text-[10px] tracking-[0.3em] uppercase text-fp-cyan font-heading font-semibold">Codice a 6 cifre</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  autoComplete="one-time-code"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="mt-2 w-full px-4 py-4 rounded-xl bg-fp-bg/60 border border-white/10 focus:border-fp-cyan focus:outline-none text-white text-center text-3xl font-heading font-black tracking-[0.4em]"
                  placeholder="• • • • • •"
                  data-testid="otp-input"
                />
              </div>
              <div className="mt-4 flex items-center justify-between text-xs text-white/50">
                <button onClick={() => setStep(0)} className="hover:text-fp-cyan inline-flex items-center gap-1" data-testid="otp-edit-email">
                  <ArrowLeft size={12} /> Modifica email
                </button>
                <button onClick={requestOtp} disabled={resendCooldown > 0}
                  className="hover:text-fp-cyan disabled:opacity-40 inline-flex items-center gap-1" data-testid="otp-resend">
                  <RefreshCw size={12} /> {resendCooldown > 0 ? `Rinvia tra ${resendCooldown}s` : "Rinvia codice"}
                </button>
              </div>
              <button onClick={verifyOtp} disabled={loading || otp.length !== 6}
                className="mt-8 w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-fp-cyan text-fp-bg font-heading font-bold tracking-wider uppercase text-sm hover:shadow-[0_0_25px_rgba(0,229,255,0.6)] disabled:opacity-40 transition-all"
                data-testid="otp-verify-btn">
                {loading ? "Verifica..." : <>Verifica codice <ArrowRight size={16} /></>}
              </button>
              <p className="mt-5 text-xs text-white/40 text-center">
                Non hai ricevuto l'email? Controlla la cartella SPAM o richiedi un nuovo codice.
              </p>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key="step-2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5">
              <div className="p-7 md:p-9 rounded-3xl border border-white/10 bg-fp-surface/50 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-5">
                  <CheckCircle2 className="text-fp-cyan" size={22} />
                  <div>
                    <div className="font-heading font-bold">{data.nome} {data.cognome}</div>
                    <div className="text-xs text-white/50">{data.email} ✓ verificata · {data.telefono}</div>
                  </div>
                  <button onClick={() => setStep(0)} className="ml-auto text-xs text-fp-cyan hover:underline" data-testid="edit-data-btn">Modifica</button>
                </div>
                <div className="flex items-end gap-2 pt-5 border-t border-white/10">
                  <span className="text-[10px] tracking-[0.3em] uppercase text-white/50 font-heading font-semibold">Totale</span>
                  <span className="ml-auto font-heading font-black text-3xl silver-gradient">{amount.toFixed(2).replace(".", ",")}€</span>
                </div>
              </div>

              <h2 className="font-heading font-bold text-xl mt-6">Scegli come pagare</h2>

              <button onClick={startOnlinePayment} disabled={loading}
                className="w-full p-6 rounded-3xl border border-fp-cyan/40 bg-gradient-to-br from-fp-cyan/10 to-transparent hover:border-fp-cyan hover:shadow-[0_0_30px_rgba(0,229,255,0.3)] transition-all text-left group disabled:opacity-50"
                data-testid="pay-online-btn">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-fp-cyan text-fp-bg flex items-center justify-center flex-shrink-0">
                    <CreditCard size={26} />
                  </div>
                  <div className="flex-1">
                    <div className="font-heading font-bold text-lg flex items-center gap-2">
                      Paga online · Consigliato
                      <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform text-fp-cyan" />
                    </div>
                    <div className="text-white/60 text-sm mt-1">Carta Visa/Mastercard, Klarna, Apple Pay. Iscrizione confermata in tempo reale.</div>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {["VISA", "Mastercard", "Klarna", "Apple Pay"].map((m) => (
                    <span key={m} className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-[10px] tracking-[0.2em] uppercase text-white/70 font-heading font-semibold">{m}</span>
                  ))}
                </div>
                {loading && <div className="mt-3 text-fp-cyan text-sm">Preparazione pagamento...</div>}
              </button>

              <button onClick={() => setStep(3)}
                className="w-full p-6 rounded-3xl border border-white/10 bg-fp-surface/40 hover:border-fp-cyan/40 transition-all text-left group"
                data-testid="pay-bonifico-btn">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-white/5 border border-white/10 text-white flex items-center justify-center flex-shrink-0">
                    <Building2 size={26} />
                  </div>
                  <div className="flex-1">
                    <div className="font-heading font-bold text-lg flex items-center gap-2">
                      Bonifico bancario
                      <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform text-fp-cyan" />
                    </div>
                    <div className="text-white/60 text-sm mt-1">Ricevi i dati bancari. L'iscrizione sarà confermata manualmente dopo l'accredito.</div>
                  </div>
                </div>
              </button>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div key="step-3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
              className="p-7 md:p-10 rounded-3xl border border-white/10 bg-fp-surface/50 backdrop-blur-sm" data-testid="bonifico-details">
              <h2 className="font-heading font-bold text-2xl">Dati per bonifico bancario</h2>
              <p className="mt-2 text-white/60 text-sm">
                Effettua il bonifico con i dati qui sotto. Ricorda di indicare nome e cognome nella causale.
                Invieremo una conferma dopo l'accredito.
              </p>
              <div className="mt-6 space-y-3">
                {[
                  { l: "Intestatario", v: settings?.bank_name || "—" },
                  { l: "IBAN", v: settings?.bank_iban || "—" },
                  { l: "Causale", v: (settings?.bank_causale || "Iscrizione Fanta Partenope").replace("[Nome Cognome]", `${data.nome} ${data.cognome}`) },
                  { l: "Importo", v: `${amount.toFixed(2).replace(".", ",")} €` },
                ].map((r) => (
                  <div key={r.l} className="flex items-center gap-3 p-4 rounded-xl bg-fp-bg/60 border border-white/10">
                    <div className="flex-1">
                      <div className="text-[10px] tracking-[0.3em] uppercase text-fp-cyan font-heading font-semibold">{r.l}</div>
                      <div className="mt-1 font-heading font-semibold text-white">{r.v}</div>
                    </div>
                    <button onClick={() => copyText(r.v)} className="p-2 text-white/60 hover:text-fp-cyan transition-colors">
                      <Copy size={18} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="mt-6 p-4 rounded-xl bg-fp-cyan/5 border border-fp-cyan/20">
                <div className="text-[10px] tracking-[0.3em] uppercase text-fp-cyan font-heading font-semibold">Importante</div>
                <p className="mt-1 text-white/70 text-sm">
                  Una volta effettuato il bonifico, inviaci la contabile a <b>fantapartenope@pec.it</b> con
                  nome, cognome, email e telefono. L'iscrizione verrà confermata manualmente.
                </p>
              </div>
              <button onClick={() => setStep(2)} className="mt-6 inline-flex items-center gap-2 text-fp-cyan text-sm hover:underline" data-testid="back-method-btn">
                <ArrowLeft size={16} /> Scegli un altro metodo
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mt-8 text-center text-xs text-white/40">
          Torna alla <Link to="/" className="text-fp-cyan hover:underline">home</Link>
        </div>
      </div>
    </div>
  );
}
